// Enhanced MediaGallery class with fixes for mobile dot indicators and swatch functionality
class MediaGallery extends HTMLElement {
  constructor() {
    super();
    this.initializeSwatchHandlers();
    this.userInitiatedScroll = false;
    this.hasInitialized = false;
    this.isInitialLoad = true;
    this.isScrolling = false;
    this.blockVariantEvents = false;
    this.isVerticalGallery = window.matchMedia('(min-width: 750px)').matches;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.initElements();
    if (!this.elements.viewer) return;

    // Initialize slider
    this.setupMobileSlider();

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      this.blockVariantEvents = true;
      this.handleUrlVariant(variantId);
      setTimeout(() => {
        this.blockVariantEvents = false;
        this.hasInitialized = true;
      }, 500);
    } else {
      this.hasInitialized = true;
    }

    document.addEventListener('variantMediaChange', (event) => {
      if (this.blockVariantEvents) return;
      const mediaId = event.detail.mediaId || event.detail.variant?.featured_media?.id;
      if (!mediaId) return;
      const matchingSlide = this.elements.slides.find(
        slide => slide.dataset.variantMedia === mediaId.toString()
      );
      if (matchingSlide) {
        this.userInitiatedScroll = true;
        const slideMediaId = matchingSlide.dataset.mediaId;
        this.updateGallery(slideMediaId, !this.isInitialLoad);
      }
    });

    requestAnimationFrame(() => {
      this.setupListeners();
      this.updateThumbnailNavigationButtons();
      if (!this.isVerticalGallery) {
        const currentSlide = this.getCurrentSlide();
        if (currentSlide) {
          this.updateDotNavigation(currentSlide.dataset.mediaId);
        }
      }
      this.isInitialLoad = false;
    });
  }

  initElements() {
    this.elements = {
      viewer: this.querySelector('[id^="GalleryViewer"]'),
      thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
      slides: Array.from(this.querySelectorAll('.product__media-item')),
      thumbnailItems: Array.from(this.querySelectorAll('.thumbnail-list__item')),
      sliderDots: Array.from(this.querySelectorAll('.slider-counter__link--dot')),
      nextButton: this.querySelector('.slider-button--next'),
      prevButton: this.querySelector('.slider-button--prev'),
      thumbnailNextButton: this.querySelector('.slider-button--next'),
      thumbnailPrevButton: this.querySelector('.slider-button--prev'),
      dotContainer: this.querySelector('.slider-counter--dots')
    };

    if (this.elements.thumbnails) {
      this.elements.thumbnails.style.overflowY = 'auto';
      this.elements.thumbnails.style.scrollBehavior = 'smooth';
    }
  }

  setupMobileSlider() {
    if (!this.elements.viewer || this.isVerticalGallery) return;
    
    // Enhanced mobile swipe detection
    let startX, startY, endX, endY;
    let startTime, endTime;
    
    // Touch start event
    this.elements.viewer.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = new Date().getTime();
    }, { passive: true });
    
    // Touch end event with swipe detection
    this.elements.viewer.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      endTime = new Date().getTime();
      
      this.handleSwipe(startX, startY, endX, endY, startTime, endTime);
    });
    
    // Listen for scroll events to update dots
    this.elements.viewer.addEventListener('scroll', this.debounce(() => {
      if (this.isVerticalGallery) return;
      
      // Get current slide based on scroll position
      const currentSlide = this.getCurrentSlide();
      if (currentSlide) {
        const mediaId = currentSlide.dataset.mediaId;
        this.updateDotNavigation(mediaId);
      }
    }, 100));
  }

  handleSwipe(startX, startY, endX, endY, startTime, endTime) {
    // Minimum swipe distance and time
    const minDistance = 50;
    const maxTime = 300;
    const timeElapsed = endTime - startTime;
    const distanceX = Math.abs(endX - startX);
    const distanceY = Math.abs(endY - startY);
    
    // Only consider horizontal swipes
    if (distanceX > distanceY && distanceX > minDistance && timeElapsed < maxTime) {
      if (endX < startX) {
        // Swipe left - go to next slide
        this.goToNextSlide();
      } else {
        // Swipe right - go to previous slide
        this.goToPrevSlide();
      }
    }
  }
  
  goToNextSlide() {
    if (this.isVerticalGallery) return;
    
    const currentSlide = this.getCurrentSlide();
    if (!currentSlide) return;
    
    const currentIndex = this.elements.slides.indexOf(currentSlide);
    const nextIndex = Math.min(currentIndex + 1, this.elements.slides.length - 1);
    
    if (nextIndex !== currentIndex) {
      const nextSlide = this.elements.slides[nextIndex];
      this.scrollToSlide(nextSlide.dataset.mediaId);
    }
  }
  
  goToPrevSlide() {
    if (this.isVerticalGallery) return;
    
    const currentSlide = this.getCurrentSlide();
    if (!currentSlide) return;
    
    const currentIndex = this.elements.slides.indexOf(currentSlide);
    const prevIndex = Math.max(currentIndex - 1, 0);
    
    if (prevIndex !== currentIndex) {
      const prevSlide = this.elements.slides[prevIndex];
      this.scrollToSlide(prevSlide.dataset.mediaId);
    }
  }

  findMediaIdForVariant(variantId) {
    const productJSON = document.getElementById('productJSON');
    if (!productJSON) return variantId;
    try {
      const product = JSON.parse(productJSON.textContent);
      const variant = product.variants.find(v => v.id.toString() === variantId);
      if (variant && variant.featured_media) {
        return variant.featured_media.id.toString();
      }
      const matchingSlide = this.elements.slides.find(slide => {
        return slide.dataset.variantMedia === variantId.toString();
      });
      if (matchingSlide) {
        return matchingSlide.dataset.mediaId;
      }
    } catch (e) {
      console.error(e);
    }
    return variantId;
  }

  handleUrlVariant(variantId) {
    if (this.isInitialLoad) return;
    const normalizedVariantId = variantId.toString().trim();
    const variantMediaId = this.findMediaIdForVariant(normalizedVariantId);
    const variantSlide = this.elements.slides.find(slide =>
      slide.dataset.mediaId === variantMediaId ||
      slide.dataset.mediaId.endsWith(variantMediaId) ||
      slide.dataset.variantMedia === variantMediaId
    );
    if (variantSlide) {
      const mediaId = variantSlide.dataset.mediaId;
      this.scrollToSlide(mediaId);
      this.elements.slides.forEach(slide => {
        slide.classList.toggle('is-active', slide === variantSlide);
      });
      const thumbnail = this.elements.thumbnailItems.find(thumb => thumb.dataset.target === mediaId);
      if (thumbnail) {
        this.setActiveThumbnail(thumbnail);
      }
      if (!this.isVerticalGallery) {
        this.updateDotNavigation(mediaId);
      }
    }
  }

  initializeSwatchHandlers() {
    // Debug logging for initialization
    console.log('Initializing swatch handlers');
    
    // Store bound method reference
    this.handleSwatchClick = this.handleSwatchClick.bind(this);
    
    // Add event delegation listener for swatch clicks
    document.addEventListener('click', this.handleSwatchClick);
    
    // Debug: Log all available swatches on initialization
    this.logAvailableSwatches();
  }

  handleSwatchClick(event) {
    // Find clicked swatch input or label
    const swatchInput = event.target.closest('.swatch-input__input');
    const swatchLabel = event.target.closest('.swatch-input__label');
    const isComplementary = event.target.closest('.complementary-product-form') !== null;
    
    if (isComplementary) {
      console.log('Ignoring complementary product swatch click');
      return;
    }
    
    // Get the relevant element (either input or its associated input if label was clicked)
    const relevantElement = swatchInput || 
      (swatchLabel && document.getElementById(swatchLabel.getAttribute('for')));

    if (!relevantElement) return;

    // Debug logging
    console.log('Swatch click detected:', {
      element: relevantElement,
      mediaId: relevantElement.getAttribute('data-variant-media'),
      classList: relevantElement.classList
    });

    const mediaId = relevantElement.getAttribute('data-variant-media');
    if (!mediaId) {
      console.log('No media ID found for swatch:', relevantElement);
      return;
    }

    // Find matching slide
    const matchingSlide = this.elements.slides.find(
      slide => slide.dataset.variantMedia === mediaId
    );

    if (matchingSlide) {
      console.log('Found matching slide:', {
        mediaId,
        slideId: matchingSlide.dataset.mediaId
      });

      // Update gallery with the matching slide
      this.userInitiatedScroll = true;
      this.updateGallery(matchingSlide.dataset.mediaId, true);
      
      // Update thumbnail if it exists
      const thumbnail = this.elements.thumbnailItems.find(
        thumb => thumb.dataset.target === matchingSlide.dataset.mediaId
      );
      
      if (thumbnail) {
        console.log('Setting active thumbnail:', thumbnail);
        this.setActiveThumbnail(thumbnail);
      }
    } else {
      console.log('No matching slide found for media ID:', mediaId);
    }
  }

  logAvailableSwatches() {
    // Debug logging for available swatches
    console.log('--- Available Swatches Debug Info ---');
    
    const swatchInputs = document.querySelectorAll('.swatch-input__input');
    console.log('Found swatch inputs:', swatchInputs.length);
    
    swatchInputs.forEach((swatch, index) => {
      console.log(`Swatch ${index + 1}:`, {
        mediaId: swatch.getAttribute('data-variant-media'),
        value: swatch.value,
        id: swatch.id
      });
    });
    
    // Only log slides if elements have been initialized
    if (this.elements && this.elements.slides) {
      console.log('--- Available Slides Debug Info ---');
      this.elements.slides.forEach((slide, index) => {
        console.log(`Slide ${index + 1}:`, {
          mediaId: slide.dataset.mediaId,
          variantMedia: slide.dataset.variantMedia
        });
      });
    } else {
      console.log('Slides not yet initialized');
    }
  }

  setupListeners() {
    // Dot navigation for mobile
    this.elements.sliderDots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault(); // Ensure no default action interferes
        if (this.isVerticalGallery) return;

        const targetSlide = this.elements.slides[index];
        if (targetSlide) {
          this.userInitiatedScroll = true;
          const targetMediaId = targetSlide.dataset.mediaId;
          if (targetMediaId) {
            this.scrollToSlide(targetMediaId);
            this.updateDotNavigation(targetMediaId); // Immediate update
          }
        }
      });
    });

    if (this.elements.viewer) {
      let scrollTimeout;
      this.elements.viewer.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.userInitiatedScroll = false;
          if (!this.isVerticalGallery) {
            const currentSlide = this.getCurrentSlide();
            if (currentSlide) {
              this.updateDotNavigation(currentSlide.dataset.mediaId);
            }
          }
          this.updateThumbnailNavigationButtons();
        }, 150);
      }, { passive: true });
    }

    if (this.elements.thumbnails) {
      this.elements.thumbnailItems.forEach(thumbnail => {
        const button = thumbnail.querySelector('button');
        if (button) {
          button.addEventListener('click', () => {
            if (this.isScrolling) return;
            const mediaId = thumbnail.dataset.target;
            this.userInitiatedScroll = true;
            this.setActiveThumbnail(thumbnail);
            this.scrollToSlide(mediaId);
          });
        }
      });
    }

    if (this.isVerticalGallery) {
      if (this.elements.thumbnailNextButton && this.elements.thumbnailPrevButton) {
        const thumbRect = this.elements.thumbnailItems[0].getBoundingClientRect();
        this.elements.thumbnailNextButton.addEventListener('click', () => {
          if (this.isScrolling) return;
          // const currentIndex = this.elements.thumbnailItems.findIndex(thumb =>
          //   thumb.classList.contains('is-active')
          // );
          // const nextIndex = Math.min(currentIndex + 1, this.elements.thumbnailItems.length - 1);
          // this.setActiveThumbnail(this.elements.thumbnailItems[nextIndex]);
          thumbnailsContainer.scrollBy({
            top: thumbRect.height,
            behavior: 'smooth',
          });
          this.isScrolling = true;
          setTimeout(() => {
            this.isScrolling = false;
            this.updateThumbnailNavigationButtons();
          }, 500);
        });

        this.elements.thumbnailPrevButton.addEventListener('click', () => {
          this.scrollThumbnails('prev');
          if (this.isScrolling) return;
          // const currentIndex = this.elements.thumbnailItems.findIndex(thumb =>
          //   thumb.classList.contains('is-active')
          // );
          // const prevIndex = Math.max(currentIndex - 1, 0);
          // this.setActiveThumbnail(this.elements.thumbnailItems[prevIndex]);
          thumbnailsContainer.scrollBy({
            top: -thumbRect.height,
            behavior: 'smooth',
          });
          this.isScrolling = true;
          setTimeout(() => {
            this.isScrolling = false;
            this.updateThumbnailNavigationButtons();
          }, 500);
        });
      }

      if (this.elements.thumbnails) {
        this.elements.thumbnails.addEventListener('scroll', this.debounce(() => {
          this.updateThumbnailNavigationButtons();
        }, 100));
      }

      this.updateThumbnailNavigationButtons();

      if (this.elements.nextButton) {
        this.elements.nextButton.addEventListener('click', () => {
          if (this.isScrolling) return;
          this.scrollThumbnails('next');  // Use the scrollThumbnails method
          this.isScrolling = true;
          setTimeout(() => {
            this.isScrolling = false;
            this.updateThumbnailNavigationButtons();
          }, 500);
        });
      }

      if (this.elements.prevButton) {
        this.elements.prevButton.addEventListener('click', () => {
          if (this.isScrolling) return;
          const thumbnailHeight = this.elements.thumbnailItems[0]?.offsetHeight || 0;
          this.elements.thumbnails.scrollBy({
            top: -thumbnailHeight,
            behavior: 'smooth'
          });
          this.isScrolling = true;
          setTimeout(() => {
            this.isScrolling = false;
            this.updateThumbnailNavigationButtons();
          }, 500);
        });
      }
    } else {
      // Mobile-specific button handlers
      if (this.elements.nextButton) {
        this.elements.nextButton.addEventListener('click', () => {
          this.goToNextSlide();
        });
      }

      if (this.elements.prevButton) {
        this.elements.prevButton.addEventListener('click', () => {
          this.goToPrevSlide();
        });
      }
    }
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  scrollThumbnails(direction) {
    if (!this.elements.thumbnails || !this.elements.thumbnailItems.length) return;
    
    const thumbnailHeight = this.elements.thumbnailItems[0].offsetHeight;
    const containerHeight = this.elements.thumbnails.clientHeight;
    const currentScroll = this.elements.thumbnails.scrollTop;
    const scrollHeight = this.elements.thumbnails.scrollHeight;
    
    const scrollAmount = direction === 'next' 
      ? Math.min(containerHeight, thumbnailHeight * 2)
      : -Math.min(containerHeight, thumbnailHeight * 2);
      
    // Calculate new scroll position
    const newScrollPosition = Math.max(0, Math.min(currentScroll + scrollAmount, scrollHeight - containerHeight));
    
    this.elements.thumbnails.scrollTo({
      top: newScrollPosition,
      behavior: 'smooth'
    });
  }

  updateThumbnailNavigationButtons() {
    if (!this.isVerticalGallery || !this.elements.thumbnails) return;

    const { scrollTop, scrollHeight, clientHeight } = this.elements.thumbnails;

    // Disable 'Prev' button if scrolled to the top
    if (this.elements.thumbnailPrevButton) {
      this.elements.thumbnailPrevButton.disabled = scrollTop === 0;
    }

    // Disable 'Next' button if scrolled to the bottom
    if (this.elements.thumbnailNextButton) {
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      this.elements.thumbnailNextButton.disabled = isAtBottom;
    }
  }

  updateGallery(mediaId, shouldScroll = false) {
    if (!mediaId) {
      console.log('No mediaId provided to updateGallery');
      return;
    }

    console.log('Updating gallery with mediaId:', mediaId);

    const targetSlide = this.elements.slides.find(slide => 
      slide.dataset.mediaId === mediaId || 
      slide.dataset.variantMedia === mediaId
    );

    if (!targetSlide) {
      console.log('No matching slide found for mediaId:', mediaId);
      return;
    }

    const slideIndex = this.elements.slides.indexOf(targetSlide);
    console.log('Found matching slide at index:', slideIndex);

    const thumbnail = this.elements.thumbnailItems.find(
      thumb => thumb.dataset.target === targetSlide.dataset.mediaId
    );

    if (thumbnail) {
      console.log('Setting active thumbnail');
      this.setActiveThumbnail(thumbnail);
    }

    if (shouldScroll) {
      console.log('Scrolling to slide');
      this.scrollToSlide(targetSlide.dataset.mediaId);
    }

    // Update active states
    this.elements.slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === slideIndex);
    });

    if (!this.isVerticalGallery) {
      this.updateDotNavigation(targetSlide.dataset.mediaId);
    }
  }

  scrollToSlide(mediaId) {
    const targetSlide = this.elements.slides.find(slide => slide.dataset.mediaId === mediaId);
    if (!targetSlide || !this.elements.viewer) return;

    if (this.isVerticalGallery) {
      targetSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      });
      // Immediately update active class and dots
      this.elements.slides.forEach(slide => {
        slide.classList.toggle('is-active', slide === targetSlide);
      });
      this.updateDotNavigation(mediaId);
    } else {
      targetSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
      // For mobile, make sure dots update immediately
      this.elements.slides.forEach(slide => {
        slide.classList.toggle('is-active', slide === targetSlide);
      });
      this.updateDotNavigation(mediaId);
    }
  }

  setActiveThumbnail(thumbnail) {
    if (!thumbnail || !this.elements.thumbnails) return;

    const thumbnailsContainer = this.elements.thumbnails;
    const thumbRect = thumbnail.getBoundingClientRect();
    const containerRect = thumbnailsContainer.getBoundingClientRect();
    const scrollTop = thumbnailsContainer.scrollTop;

    // Calculate offsets
    const thumbnailOffset = thumbRect.top - containerRect.top + scrollTop;
    const containerHeight = thumbnailsContainer.clientHeight;
    const thumbnailHeight = thumbRect.height;
    const targetOffset = thumbnailOffset - (containerHeight - thumbnailHeight) / 2;

    const maxScroll = thumbnailsContainer.scrollHeight - containerHeight;
    const finalScroll = Math.max(0, Math.min(targetOffset, maxScroll));

    // Debugging Logs
    console.log('--- Debugging setActiveThumbnail ---');
    console.log('Thumbnail Rect:', thumbRect);
    console.log('Container Rect:', containerRect);
    console.log('Scroll Top:', scrollTop);
    console.log('Thumbnail Offset:', thumbnailOffset);
    console.log('Target Offset (centered position):', targetOffset);
    console.log('Max Scroll Allowed:', maxScroll);
    console.log('Final Scroll Position:', finalScroll);

    // Scroll the thumbnails container to the calculated position
    thumbnailsContainer.scrollTo({
      top: finalScroll,
      behavior: 'smooth',
    });

    // Update active state for thumbnails
    this.elements.thumbnailItems.forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb === thumbnail);
    });

    console.log('Thumbnail scrolled and marked as active.');
  }

  updateDotNavigation(mediaId) {
    if (this.isVerticalGallery || !this.elements.sliderDots.length) return;
    
    const slideIndex = this.elements.slides.findIndex(
      slide => slide.dataset.mediaId === mediaId
    );
    
    if (slideIndex > -1) {
      this.elements.sliderDots.forEach((dot, index) => {
        // Update ARIA attributes
        dot.setAttribute('aria-current', index === slideIndex ? 'true' : 'false');
        dot.classList.toggle('slider-counter__link--active', index === slideIndex);
        
        // Find and update the dot element
        const dotSpan = dot.querySelector('.dot');
        if (dotSpan) {
          // First remove active class from all dots
          dotSpan.classList.remove('active');
          
          // Then add active class to current dot
          if (index === slideIndex) {
            dotSpan.classList.add('active');
          }
        }
      });
    }
  }

  getCurrentSlide() {
    if (!this.elements.viewer || !this.elements.slides.length) return null;
    
    const viewerRect = this.elements.viewer.getBoundingClientRect();
    const center = this.isVerticalGallery
      ? viewerRect.top + viewerRect.height / 2
      : viewerRect.left + viewerRect.width / 2;
    
    let closestSlide = null;
    let minDistance = Infinity;
    
    this.elements.slides.forEach(slide => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = this.isVerticalGallery
        ? rect.top + rect.height / 2
        : rect.left + rect.width / 2;
      
      const distance = Math.abs(slideCenter - center);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestSlide = slide;
      }
    });
    
    return closestSlide;
  }

  // Cleanup method
  disconnectedCallback() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.handleSwatchClick);
  }
}

// Register the custom element
customElements.define('media-gallery', MediaGallery);

// Enhanced swatch handler class
class EnhancedSwatchGalleryHandler {
  constructor() {
    this.initialized = false;
    this.variantImageMap = new Map();
    this.initializeWhenReady();
  }

  async initializeWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      setTimeout(() => this.initialize(), 500);
    }
  }

  initialize() {
    this.mediaGallery = document.querySelector('media-gallery');
    
    if (!this.mediaGallery) {
      console.error('Media gallery not found');
      return;
    }

    if (!this.mediaGallery.elements || !this.mediaGallery.elements.slides) {
      setTimeout(() => this.initialize(), 100);
      return;
    }

    // Build the variant-image mapping
    this.buildVariantImageMap();
    
    console.log('Media gallery initialized with slides:', this.mediaGallery.elements.slides.length);
    
    this.initializeEventListeners();
    this.logAvailableSwatches();
    this.initialized = true;
  }

  buildVariantImageMap() {
    // Try to get product data from the page
    const productJSON = document.getElementById('productJSON');
    if (productJSON) {
      try {
        const product = JSON.parse(productJSON.textContent);
        product.variants.forEach(variant => {
          if (variant.featured_media) {
            this.variantImageMap.set(variant.id.toString(), variant.featured_media.id.toString());
          }
        });
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }

    // Map all media items from the gallery
    if (this.mediaGallery?.elements?.slides) {
      this.mediaGallery.elements.slides.forEach(slide => {
        const variantId = slide.dataset.variantMedia;
        const mediaId = slide.dataset.mediaId;
        if (variantId && mediaId) {
          this.variantImageMap.set(variantId, mediaId);
        }
      });
    }

    console.log('Variant-Image map built:', this.variantImageMap);
  }

  initializeEventListeners() {
    document.removeEventListener('click', this.handleSwatchClickBound);
    document.removeEventListener('change', this.handleSwatchChangeBound);
    
    this.handleSwatchClickBound = this.handleSwatchClick.bind(this);
    this.handleSwatchChangeBound = this.handleSwatchChange.bind(this);
    
    document.addEventListener('click', this.handleSwatchClickBound);
    document.addEventListener('change', this.handleSwatchChangeBound);
  }

  handleSwatchClick(event) {
    if (!(document.body.classList.contains('why-barely-zero-page') && event.target.nodeName.toLowerCase() === 'a')) {
      event.preventDefault();
    }

    const swatchInput = event.target.closest('.swatch-input__input');
    const swatchLabel = event.target.closest('.swatch-input__label');
    
    if (!swatchInput && !swatchLabel) return;
    
    const relevantElement = swatchInput || 
      (swatchLabel && document.getElementById(swatchLabel.getAttribute('for')));

    if (!relevantElement) return;

    const input = swatchInput || (swatchLabel && document.getElementById(swatchLabel.getAttribute('for')));
    if (!input) return;

    console.log('Swatch click detected:', {
      element: input,
      mediaId: input.dataset.variantMedia,
      value: input.value,
      dataset: input.dataset
    });

    this.handleSwatchSelection(input);
  }

  handleSwatchChange(event) {
    const input = event.target;
    if (!input.classList.contains('swatch-input__input')) return;

    this.handleSwatchSelection(input);
  }

  handleSwatchSelection(input) {
    const variantMediaId = input.dataset.variantMedia;
    if (!variantMediaId) {
      console.warn('No variant media ID found for swatch');
      return;
    }

    // Try to find matching slide using multiple methods
    const matchingSlide = this.findGalleryItem(variantMediaId);
    
    if (matchingSlide) {
      console.log('Found matching slide:', {
        swatchMediaId: variantMediaId,
        slideMediaId: matchingSlide.dataset.mediaId,
        slideVariantMedia: matchingSlide.dataset.variantMedia
      });

      // Update gallery
      if (this.mediaGallery && typeof this.mediaGallery.updateGallery === 'function') {
        this.mediaGallery.userInitiatedScroll = true;
        this.mediaGallery.updateGallery(matchingSlide.dataset.mediaId, true);
        
        // Dispatch event for variant change
        const event = new CustomEvent('variantMediaChange', {
          detail: {
            mediaId: variantMediaId,
            variant: {
              featured_media: {
                id: variantMediaId
              }
            }
          }
        });
        document.dispatchEvent(event);
      }
    } else {
      // Try to find through the variant map
      const mappedMediaId = this.variantImageMap.get(variantMediaId);
      if (mappedMediaId) {
        const slideByMappedId = this.findGalleryItemByMappedId(mappedMediaId);
        if (slideByMappedId) {
          console.log('Found slide through variant mapping:', {
            originalId: variantMediaId,
            mappedId: mappedMediaId,
            slide: slideByMappedId
          });
          this.mediaGallery.updateGallery(slideByMappedId.dataset.mediaId, true);
        }
      } else {
        console.warn('No matching slide or mapping found for variant media ID:', variantMediaId);
      }
    }
  }

  findGalleryItem(mediaId) {
    if (!this.mediaGallery?.elements?.slides) {
      console.error('Media gallery or slides not initialized');
      return null;
    }

    return this.mediaGallery.elements.slides.find(slide => {
      // Try different matching strategies
      if (slide.dataset.variantMedia === mediaId) return true;
      if (slide.dataset.mediaId === mediaId) return true;
      if (slide.dataset.mediaId.split('__main-').pop() === mediaId) return true;
      if (slide.dataset.mediaId.endsWith(mediaId)) return true;
      return false;
    });
  }

  findGalleryItemByMappedId(mediaId) {
    if (!this.mediaGallery?.elements?.slides) return null;
    
    return this.mediaGallery.elements.slides.find(slide => {
      const slideMediaId = slide.dataset.mediaId.split('__main-').pop();
      return slideMediaId === mediaId || slide.dataset.variantMedia === mediaId;
    });
  }

  logAvailableSwatches() {
    console.log('--- Available Swatches Debug Info ---');
    
    const swatchInputs = document.querySelectorAll('.swatch-input__input');
    console.log('Found swatch inputs:', swatchInputs.length);
    
    swatchInputs.forEach((swatch, index) => {
      console.log(`Swatch ${index + 1}:`, {
        mediaId: swatch.dataset.variantMedia,
        value: swatch.value,
        id: swatch.id,
        fullDataset: swatch.dataset
      });
    });

    if (this.mediaGallery?.elements?.slides) {
      console.log('--- Available Slides Debug Info ---');
      this.mediaGallery.elements.slides.forEach((slide, index) => {
        console.log(`Slide ${index + 1}:`, {
          mediaId: slide.dataset.mediaId,
          variantMedia: slide.dataset.variantMedia,
          fullDataset: slide.dataset
        });
      });
    }

    console.log('--- Variant-Image Map ---');
    console.log(Object.fromEntries(this.variantImageMap));
  }

  destroy() {
    if (this.handleSwatchClickBound) {
      document.removeEventListener('click', this.handleSwatchClickBound);
    }
    if (this.handleSwatchChangeBound) {
      document.removeEventListener('change', this.handleSwatchChangeBound);
    }
  }
}

// Initialize and add to window for debugging
const enhancedSwatchHandler = new EnhancedSwatchGalleryHandler();
window.enhancedSwatchHandler = enhancedSwatchHandler;
